// libraries.
import { useFormContext } from 'react-hook-form';

// hooks.
import { useAutocompleteConfidence } from '@domains/documents-review/hooks/useAutocompleteConfidence';
import { useFieldRequirements } from '@domains/documents-review/hooks/useFieldRequirements';

// domain.
import { CountryCode } from '@domains/country';

// components.
import { InputField } from '@shared/InputField/InputField';
import { NumericField } from '@shared/NumericField/NumericField';
import { DropdownField } from '@shared/DropdownField/DropdownField';
import { PriceInputField } from '@shared/PriceInputField/PriceInputField';

// constants.
import { DROPDOWN_OPTIONS } from '../../constants';
import { AccessoryAreasField } from '../../shared/AccessoryAreasField/AccessoryAreasField';

export const FieldsMX = () => {
  const { watch } = useFormContext();
  const { getConfidenceProps } = useAutocompleteConfidence();
  // Obligatoriedad desde el servicio field_requirements (fallback a la config actual).
  const { isRequired } = useFieldRequirements(CountryCode.MX);

  const hasGarageDoc = watch('hasGarageDoc');
  const isCondominium = Number(watch('propertyType')) === 3;

  return (
    <>
      <InputField
        label="Conjunto"
        name="complex"
        placeholder="Nombre del conjunto"
        required={isRequired('complex')}
        {...getConfidenceProps('complex')}
      />
      <div className="d-flex gap-2">
        <DropdownField
          label="Tipo de Inmueble"
          name="propertyType"
          options={DROPDOWN_OPTIONS.PROPERTY_TYPE}
          required={isRequired('propertyType')}
          {...getConfidenceProps('propertyType')}
        />
        {isCondominium && (
          <PriceInputField
            label="Valor admin."
            name="adminPrice"
            required={isRequired('adminPrice')}
            {...getConfidenceProps('adminPrice')}
          />
        )}
      </div>
      <div className="d-flex gap-2">
        <InputField
          label="Zona mediana"
          name="medianZone"
          placeholder="Escribir"
          required={isRequired('medianZone')}
          {...getConfidenceProps('medianZone')}
        />
        <InputField
          label="Código postal"
          name="zipCode"
          placeholder="Escribir"
          required={isRequired('zipCode')}
          {...getConfidenceProps('zipCode')}
        />
      </div>
      <DropdownField
        label="Tipo de documento para determinar el área"
        name="documentType"
        options={DROPDOWN_OPTIONS.PROPERTY_DOCUMENT_TYPE_OPTIONS}
        required={isRequired('documentType')}
        {...getConfidenceProps('documentType')}
      />
      <div className="d-flex gap-2">
        <InputField
          label="Área precio comité"
          maxLength={3}
          name="committeePriceArea"
          pattern={/^\d{1,3}$/}
          placeholder="Área"
          subLabel="m²"
          required={isRequired('committeePriceArea')}
          {...getConfidenceProps('committeePriceArea')}
        />
        <InputField
          label="Área terreno comité"
          maxLength={3}
          name="committeeLandArea"
          pattern={/^\d{1,3}$/}
          placeholder="Área"
          subLabel="m²"
          required={isRequired('committeeLandArea')}
          {...getConfidenceProps('committeeLandArea')}
        />
      </div>
      <InputField
        label="Comité área de ampliación"
        maxLength={3}
        name="committeeExpansionArea"
        pattern={/^\d{1,3}$/}
        placeholder="Área"
        subLabel="m²"
        required={isRequired('committeeExpansionArea')}
        {...getConfidenceProps('committeeExpansionArea')}
      />
      <div className="d-flex gap-2">
        <NumericField
          label="Piso"
          name="floor"
          placeholder="Piso"
          required={isRequired('floor')}
          {...getConfidenceProps('floor')}
        />
        <NumericField
          label="Recamaras"
          name="rooms"
          placeholder="Recamaras"
          required={isRequired('rooms')}
          {...getConfidenceProps('rooms')}
        />
      </div>
      <div className="d-flex gap-2">
        <NumericField
          label="Baños"
          name="baths"
          placeholder="Baños"
          required={isRequired('baths')}
          {...getConfidenceProps('baths')}
        />
        <NumericField
          label="Medios baños"
          name="halfBaths"
          placeholder="Medios baños"
          required={isRequired('halfBaths')}
          {...getConfidenceProps('halfBaths')}
        />
      </div>
      <div className="d-flex gap-2">
        <NumericField
          label="Elevador"
          name="elevator"
          placeholder="Elevador"
          required={isRequired('elevator')}
          {...getConfidenceProps('elevator')}
        />
        <InputField
          label="Antigüedad"
          maxLength={3}
          name="yearsOld"
          pattern={/^\d{1,3}$/}
          placeholder="Antigüedad"
          subLabel="años"
          required={isRequired('yearsOld')}
          {...getConfidenceProps('yearsOld')}
        />
      </div>
      <div className="d-flex gap-2">
        <DropdownField
          label="Documento estacio."
          name="hasGarageDoc"
          options={DROPDOWN_OPTIONS.GARAGE}
          required={isRequired('hasGarageDoc')}
          {...getConfidenceProps('hasGarageDoc')}
        />
        {hasGarageDoc === 'Si' && (
          <NumericField
            label="Estacionamiento"
            name="garage"
            placeholder="Estacionamiento"
            required={isRequired('garage')}
            {...getConfidenceProps('garage')}
          />
        )}
      </div>
      {hasGarageDoc === 'Si' && (
        <DropdownField
          label="Estacionamiento con doble clave o escritura"
          name="parkingWithDoubleKeyOrDeed"
          options={DROPDOWN_OPTIONS.YES_NO}
          required={isRequired('parkingWithDoubleKeyOrDeed')}
          {...getConfidenceProps('parkingWithDoubleKeyOrDeed')}
        />
      )}
      <hr />
      <AccessoryAreasField />
    </>
  );
};
